<template>
  <div class="report-page">
    <ReportHeaderTab />

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <h5 class="page-title">Inventory Status Report</h5>
        <p class="page-sub">Monitor current stock balances by product and store</p>
      </div>

      <div class="filters-wrap">

        <!-- From -->
        <div class="filter-group">
          <div class="filter-label">From</div>
          <div class="date-input-wrap">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker v-model="tableData.start_date" :enableTimePicker="false" class="date-picker" />
          </div>
        </div>

        <div class="filter-sep">—</div>

        <!-- To -->
        <div class="filter-group">
          <div class="filter-label">To</div>
          <div class="date-input-wrap">
            <font-awesome-icon icon="calendar" class="date-icon" />
            <Datepicker v-model="tableData.end_date" :enableTimePicker="false" class="date-picker" />
          </div>
        </div>

        <!-- Product -->
        <div class="filter-group">
          <div class="filter-label">Product</div>
          <Multiselect
            v-model="tableData.product_id"
            label="name"
            :options="getProducts"
            placeholder="All Products"
            :searchable="true"
            :create-option="false"
            :close-on-select="true"
            class="filter-multiselect"
          />
        </div>

        <!-- Store -->
        <div class="filter-group">
          <div class="filter-label">Store</div>
          <Multiselect
            v-model="tableData.store_id"
            label="name"
            :options="getStores"
            placeholder="All Stores"
            :searchable="true"
            :create-option="false"
            :close-on-select="true"
            class="filter-multiselect"
          />
        </div>

        <!-- Apply -->
        <button class="btn-apply" :disabled="crudLoading" @click="fetchData()">
          <font-awesome-icon :icon="crudLoading ? 'spinner' : 'filter'" :spin="crudLoading" />
          {{ crudLoading ? 'Applying...' : 'Apply' }}
        </button>

        <!-- Export -->
        <div class="dropdown">
          <button class="btn-outline" @click="exportOpen = !exportOpen">
            <font-awesome-icon icon="download" /> Export
          </button>
          <ul class="dropdown-menu" :class="{ show: exportOpen }">
            <li>
              <a class="dropdown-item" href="#" @click.prevent="exportPdf">
                <font-awesome-icon icon="file-pdf" class="text-danger me-2" /> PDF
              </a>
            </li>
            <li>
              <a class="dropdown-item" href="#" @click.prevent="exportCsv">
                <font-awesome-icon icon="file-csv" class="text-success me-2" /> CSV
              </a>
            </li>
          </ul>
        </div>

      </div>
    </div>

    <!-- Summary cards -->
    <div class="summary-grid" v-if="details.length > 0 && !loading">
      <div class="summary-card">
        <div class="summary-icon summary-icon--blue">
          <font-awesome-icon icon="cubes" />
        </div>
        <div>
          <div class="summary-label">Total Records</div>
          <div class="summary-value">{{ pagination.total }}</div>
        </div>
      </div>
      <div class="summary-card">
        <div class="summary-icon summary-icon--green">
          <font-awesome-icon icon="boxes" />
        </div>
        <div>
          <div class="summary-label">Total Stock</div>
          <div class="summary-value">{{ totalStock }}</div>
        </div>
      </div>
      <div class="summary-card" v-if="lowStockCount > 0">
        <div class="summary-icon summary-icon--red">
          <font-awesome-icon icon="exclamation-triangle" />
        </div>
        <div>
          <div class="summary-label">Low Stock (&lt; 5)</div>
          <div class="summary-value low-stock-val">{{ lowStockCount }}</div>
        </div>
      </div>
    </div>

    <!-- Data card -->
    <div class="data-card">

      <!-- Loading -->
      <div v-if="loading" class="state-block">
        <div class="spinner-border text-primary" style="width:3rem;height:3rem;" role="status"></div>
        <p class="mt-3 text-muted-cell">Loading inventory data...</p>
      </div>

      <!-- Empty -->
      <div v-else-if="details.length === 0" class="state-block">
        <div class="state-icon">
          <font-awesome-icon icon="cubes" />
        </div>
        <p class="state-title">No inventory records found</p>
        <p class="state-sub">Try adjusting your date range, product or store filter and click Apply.</p>
      </div>

      <template v-else>

        <!-- Toolbar -->
        <div class="table-toolbar">
          <span class="table-count">
            Showing <strong>{{ pagination.from }}–{{ pagination.to }}</strong>
            of <strong>{{ pagination.total }}</strong> records
          </span>
          <div class="toolbar-right">
            <label class="table-count">Rows:</label>
            <select v-model="tableData.length" @change="tableData.page = 1; fetchData()" class="rows-select">
              <option value="5">5</option>
              <option value="10">10</option>
              <option value="20">20</option>
              <option value="30">30</option>
              <option value="50">50</option>
              <option value="100">100</option>
              <option :value="pagination.total">All</option>
            </select>
          </div>
        </div>

        <!-- Table -->
        <div class="table-wrap">
          <table class="data-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Store</th>
                <th>Product</th>
                <th>Date</th>
                <th class="text-right">Stock Balance</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in details" :key="item.id">

                <td class="text-muted-cell">{{ pagination.from + index }}</td>

                <td>
                  <div class="store-cell">
                    <div class="store-icon">
                      <font-awesome-icon icon="store" />
                    </div>
                    <span class="store-name">{{ item.store_name }}</span>
                  </div>
                </td>

                <td class="fw-500">{{ item.product_name }}</td>

                <td class="text-muted-cell" style="white-space:nowrap;">{{ item.created_date }}</td>

                <td class="text-right fw-bold" :class="stockClass(item.stock_balance)">
                  {{ item.stock_balance }}
                </td>

                <td>
                  <span class="stock-badge" :class="stockBadgeClass(item.stock_balance)">
                    <font-awesome-icon :icon="stockIcon(item.stock_balance)" class="me-1" />
                    {{ stockLabel(item.stock_balance) }}
                  </span>
                </td>

              </tr>
            </tbody>
          </table>
        </div>

        <!-- Pagination -->
        <div class="table-footer">
          <pagination
            :pagination="pagination"
            @prev="tableData.page = pagination.currentPage - 1; fetchData()"
            @next="tableData.page = pagination.currentPage + 1; fetchData()"
          />
        </div>

      </template>
    </div>

  </div>
</template>

<script>
import axios from 'axios';
import { useToast } from 'vue-toastification';
import { reusableMixin } from '../../../lib/reusableMixin';
import Multiselect from '@vueform/multiselect';
import ReportHeaderTab from './components/reportheadertab.vue';

export default {
  name: 'InventoryStatusReport',

  components: { ReportHeaderTab, Multiselect },

  mixins: [reusableMixin],

  data() {
    return {
      print_content:     [],
      headerArrayColumn: ['S/N', 'Store', 'Product', 'Date', 'Quantity'],
      footerRowArray:    [],

      details:     [],
      products:    [],
      getProducts: [],
      stores:      [],
      getStores:   [],
      loading:     false,
      crudLoading: false,
      exportOpen:  false,

      tableData: { page: 1,
        store_id:   '',
        product_id: '',
        start_date: new Date(),
        end_date:   new Date(),
        draw:       0,
        length:     10,
        search:     '',
        column:     0,
        dir:        'desc',
      },

      pagination: {
        lastPage: '', currentPage: '', total: '',
        lastPageUrl: '', nextPageUrl: '', prevPageUrl: '',
        from: 1, to: '',
      },

      toast: useToast(),
    };
  },

  computed: {
    totalStock() {
      return this.details.reduce((s, d) => s + (parseInt(d.stock_balance) || 0), 0);
    },
    lowStockCount() {
      return this.details.filter(d => parseInt(d.stock_balance) < 5).length;
    },
  },

  methods: {
    // ── Stock status helpers ─────────────────────────────────────────────
    stockClass(qty) {
      const q = parseInt(qty) || 0;
      if (q === 0)  return 'out-of-stock';
      if (q < 5)    return 'low-stock';
      return 'in-stock';
    },

    stockBadgeClass(qty) {
      const q = parseInt(qty) || 0;
      if (q === 0)  return 'stock-badge--out';
      if (q < 5)    return 'stock-badge--low';
      return 'stock-badge--ok';
    },

    stockIcon(qty) {
      const q = parseInt(qty) || 0;
      if (q === 0) return 'times-circle';
      if (q < 5)   return 'exclamation-triangle';
      return 'check-circle';
    },

    stockLabel(qty) {
      const q = parseInt(qty) || 0;
      if (q === 0) return 'Out of Stock';
      if (q < 5)   return 'Low Stock';
      return 'In Stock';
    },

    // ── Fetch data ───────────────────────────────────────────────────────
    async allStores() {
      try {
        const { data } = await axios.get('/admin/store');
        this.getStores = data.data.map(s => ({ value: s.id, name: s.store_name }));
      } catch (_) {
        this.toast.error('Could not load stores.');
      }
    },

    async allProducts() {
      try {
        const { data } = await axios.get('/inventory/product');
        this.getProducts = data.data.map(p => ({ value: p.id, name: p.product_name }));
      } catch (_) {
        this.toast.error('Could not load products.');
      }
    },

    // ── Export ──────────────────────────────────────────────────────────
    exportPdf() {
      this.exportOpen = false;
      this.drawPdf(
        this.print_content, this.headerArrayColumn,
        'l', 'inventory-status-report', 'Inventory Status Report', '',
      );
    },

    exportCsv() {
      this.exportOpen = false;
      this.reusableMixinToCSV('inventory-status-report');
    },

    // ── Data ────────────────────────────────────────────────────────────
    dataToPrint(details) {
      this.print_content = details.map((d, idx) => ({
        sn:       idx + 1,
        store:    d.store_name,
        product:  d.product_name,
        date:     d.created_date,
        quantity: d.stock_balance,
      }));
    },

    fetchData(url = '/report/inventory-status-report') {
      this.loading     = true;
      this.crudLoading = true;
      this.tableData.draw++;

      axios.get(url, { params: this.tableData })
        .then(({ data }) => {
          this.details = data.data.data;
          this.dataToPrint(this.details);
          this.configPagination(data.data);
        })
        .catch(() => {
          this.toast.error('Failed to load inventory data. Please try again.');
        })
        .finally(() => {
          this.loading     = false;
          this.crudLoading = false;
        });
    },

    configPagination(data) {
      this.pagination.lastPage    = data.last_page;
      this.pagination.currentPage = data.current_page;
      this.pagination.total       = data.total;
      this.pagination.lastPageUrl = data.last_page_url;
      this.pagination.nextPageUrl = data.next_page_url;
      this.pagination.prevPageUrl = data.prev_page_url;
      this.pagination.from        = data.from;
      this.pagination.to          = data.to;
    },
  },

  created() {
    this.fetchData();
    this.allStores();
    this.allProducts();
  },
};
</script>

<style scoped>
/* ── Page ────────────────────────────────────────────────────────────── */
.report-page { display:flex; flex-direction:column; gap:18px; }

/* ── Header ──────────────────────────────────────────────────────────── */
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }

/* ── Filters ─────────────────────────────────────────────────────────── */
.filters-wrap     { display:flex; align-items:flex-end; flex-wrap:wrap; gap:10px; }
.filter-group     { display:flex; flex-direction:column; gap:4px; }
.filter-label     { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
.filter-sep       { color:#b0bdd0; font-size:16px; margin-bottom:4px; }
.filter-multiselect { min-width:160px; }
.date-input-wrap  { position:relative; display:flex; align-items:center; }
.date-icon        { position:absolute; left:10px; color:#7a90b0; font-size:12px; pointer-events:none; z-index:1; }
.date-picker      { background:none; border:none; padding-left:28px; }
.btn-apply        { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-apply:hover:not(:disabled) { background:#003b8e; }
.btn-apply:disabled { opacity:.55; cursor:not-allowed; }
.btn-outline      { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-outline:hover { background:#f4f7fb; }
.dropdown         { position:relative; }
.dropdown-menu    { position:absolute; top:calc(100% + 6px); right:0; background:#fff; border:1px solid #e4eaf2; border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,.1); padding:6px; min-width:160px; z-index:200; display:none; }
.dropdown-menu.show { display:block; }
.dropdown-item    { display:flex; align-items:center; gap:8px; padding:8px 12px; font-size:13px; border-radius:7px; color:#1a2b45; cursor:pointer; text-decoration:none; }
.dropdown-item:hover { background:#f4f7fb; }
.me-1 { margin-right:4px; }
.me-2 { margin-right:8px; }

/* ── Summary cards ───────────────────────────────────────────────────── */
.summary-grid        { display:grid; grid-template-columns:repeat(3,1fr); gap:14px; max-width:680px; }
.summary-card        { background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:18px 20px; display:flex; align-items:center; gap:16px; }
.summary-icon        { width:44px; height:44px; border-radius:12px; display:flex; align-items:center; justify-content:center; font-size:18px; flex-shrink:0; }
.summary-icon--blue  { background:rgba(0,74,173,.1); color:#004AAD; }
.summary-icon--green { background:#f0fdf4; color:#16a34a; }
.summary-icon--red   { background:#fff5f5; color:#e03535; }
.summary-label       { font-size:12px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; margin-bottom:5px; }
.summary-value       { font-size:20px; font-weight:700; color:#1a2b45; }
.low-stock-val       { color:#e03535; }

/* ── Data card ───────────────────────────────────────────────────────── */
.data-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }

/* ── States ──────────────────────────────────────────────────────────── */
.state-block { display:flex; flex-direction:column; align-items:center; justify-content:center; padding:64px 24px; text-align:center; }
.state-icon  { width:60px; height:60px; border-radius:16px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:24px; margin-bottom:16px; }
.state-title { font-size:16px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
.state-sub   { font-size:13px; color:#7a90b0; margin:0; }
.text-muted-cell { color:#4a5e78; }
.mt-3 { margin-top:12px; }

/* ── Table toolbar ───────────────────────────────────────────────────── */
.table-toolbar  { display:flex; align-items:center; justify-content:space-between; padding:14px 20px; border-bottom:1px solid #e4eaf2; flex-wrap:wrap; gap:10px; }
.table-count    { font-size:13px; color:#7a90b0; }
.table-count strong { color:#1a2b45; }
.toolbar-right  { display:flex; align-items:center; gap:8px; }
.rows-select    { border:1px solid #dde5f0; border-radius:7px; padding:5px 10px; font-size:13px; color:#1a2b45; background:#f8fafc; outline:none; cursor:pointer; }

/* ── Table ───────────────────────────────────────────────────────────── */
.table-wrap  { overflow-x:auto; }
.data-table  { width:100%; border-collapse:collapse; }
.data-table thead tr { background:#f8fafc; }
.data-table thead th { font-size:11.5px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:12px 16px; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .12s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody td { padding:12px 16px; font-size:13px; vertical-align:middle; }
.text-right { text-align:right; }
.fw-500     { font-weight:500; }
.fw-bold    { font-weight:700; }

/* ── Store cell ──────────────────────────────────────────────────────── */
.store-cell  { display:flex; align-items:center; gap:9px; }
.store-icon  { width:28px; height:28px; border-radius:7px; background:rgba(0,74,173,.08); color:#004AAD; display:flex; align-items:center; justify-content:center; font-size:12px; flex-shrink:0; }
.store-name  { font-size:13px; font-weight:500; color:#1a2b45; }

/* ── Stock balance colouring ─────────────────────────────────────────── */
.in-stock    { color:#16a34a; }
.low-stock   { color:#e08c00; }
.out-of-stock { color:#e03535; }

/* ── Stock status badges ─────────────────────────────────────────────── */
.stock-badge      { display:inline-flex; align-items:center; border-radius:6px; padding:3px 10px; font-size:12px; font-weight:600; border:1px solid transparent; white-space:nowrap; }
.stock-badge--ok  { background:#f0fdf4; color:#16a34a; border-color:#bbf7d0; }
.stock-badge--low { background:#fff7ed; color:#e08c00; border-color:#fed7aa; }
.stock-badge--out { background:#fff5f5; color:#e03535; border-color:#fecaca; }

/* ── Footer ──────────────────────────────────────────────────────────── */
.table-footer { padding:14px 20px; border-top:1px solid #e4eaf2; display:flex; justify-content:flex-end; }

/* ── Responsive ──────────────────────────────────────────────────────── */
@media (max-width:768px) {
  .page-header, .filters-wrap { flex-direction:column; align-items:stretch; }
  .filter-sep     { display:none; }
  .filter-multiselect { width:100%; }
  .summary-grid   { grid-template-columns:1fr; max-width:100%; }
}
</style>